import React, { useEffect, useRef, useState } from "react";
import "./Testimonial.css";

/**
 * Testimonial slider (no external libs)
 * - 2 cards per "slide" on desktop, 1 per slide on mobile
 * - Autoplay, click dots to navigate, arrow keys supported
 * - Montserrat, soft lavender UI as per screenshot
 * - Avatar completely removed
 */

const DATA = [
  {
    quote: "I experienced the best massage EVER at A Peaceful Retreat in Puyallup. Angela Oaks is knowledgeable, professional, thoughtful, and has a talent for finding and working out tension spots. If you are a massage connoisseur, don't miss out on this.",
    name: "KELLI C",
    role: "Client",
    date: "July 2, 2024"
  },
  {
    quote: "Angela has magical hands. Very attentive to your needs. Best massage I've had. She's very attentive to my needs and continues to impress with her professional approach.",
    name: "SAMANTHA J",
    role: "Regular Client",
    date: "March 3, 2024"
  },
  {
    quote: "I've been a client of Angela's for almost two years. She is professional, cares about her clients, easy to work with and continues to grow in knowledge and skill in her field. I believe she's one of the best massage therapist in the area.",
    name: "KATHRYN T",
    role: "Long-term Client",
    date: "May 23, 2023"
  },
  {
    quote: "Angela gives the best massages! I'll never switch to another practitioner! My whole family sees her. She's truly amazing and provides consistent, professional service every time.",
    name: "HANNAH S",
    role: "Family Client",
    date: "February 13, 2023"
  },
  {
    quote: "Very nice, clean, with an overall relaxing atmosphere. Angela is truly amazing, I can't express it enough. She's a life saver and one of the best in the business. I can't recommend her enough.",
    name: "LESSIE M",
    role: "Client",
    date: "May 13, 2022"
  },
  {
    quote: "Angela gave me one of the best massages I've ever had, she's really amazing! She applied the pressure I was seeking and gave me much needed relief in my back area. She made me feel very comfortable and relaxed!",
    name: "LESSIE M",
    role: "Returning Client",
    date: "May 2, 2022"
  },
  {
    quote: "Angela is very attentive to my needs and my problem areas, especially with my line of work. Her passion in her work shows. Highly recommend! ❤",
    name: "PATRICIA V",
    role: "Professional Client",
    date: "March 18, 2021"
  },
  {
    quote: "Fantastic massage! Angela was just the kind of masseuse anyone would enjoy. So relaxing and invigorating! The atmosphere is peaceful and the service is exceptional.",
    name: "JILL C",
    role: "Client",
    date: "September 18, 2019"
  }
];

export default function Testimonial() {
  const [index, setIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const timerRef = useRef(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 900);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const groupSize = isMobile ? 1 : 2;
  const slides = [];
  for (let i = 0; i < DATA.length; i += groupSize) {
    slides.push(DATA.slice(i, i + groupSize));
  }

  useEffect(() => {
    if (isHovering) return;
    
    timerRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timerRef.current);
  }, [slides.length, isHovering]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") setIndex((i) => (i + 1) % slides.length);
      if (e.key === "ArrowLeft") setIndex((i) => (i - 1 + slides.length) % slides.length);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [slides.length]);

  const goToSlide = (newIndex) => {
    setIndex(newIndex);
  };

  const goToNext = () => {
    setIndex((i) => (i + 1) % slides.length);
  };

  const goToPrev = () => {
    setIndex((i) => (i - 1 + slides.length) % slides.length);
  };

  return (
    <section className="tst" aria-labelledby="tst-title" ref={sectionRef}>
      <div className="tst__bg-elements" aria-hidden="true">
        <div className="tst__bg-shape tst__bg-shape--1"></div>
        <div className="tst__bg-shape tst__bg-shape--2"></div>
        <div className="tst__bg-shape tst__bg-shape--3"></div>
      </div>

      <div className="tst__container">
        <div className={`tst__head ${isVisible ? 'is-visible' : ''}`}>
          <div className="tst__eyebrow">
            <span className="tst__eyebrow-text">Trusted by my Clients</span>
          </div>
          <h2 id="tst-title" className="tst__title">
            <span className="tst__title-line">Stories From </span>
            <span className="tst__title-line tst__title-line--accent">Those I Serve</span>
          </h2>
          <div className="tst__rule" aria-hidden="true">
            <div className="tst__rule-line"></div>
            <div className="tst__rule-dot"></div>
            <div className="tst__rule-line"></div>
          </div>
        </div>

        <div 
          className="tst__slider-container"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          <button 
            className="tst__nav tst__nav--prev"
            onClick={goToPrev}
            aria-label="Previous testimonials"
          >
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" fill="currentColor"/>
            </svg>
          </button>
          
          <button 
            className="tst__nav tst__nav--next"
            onClick={goToNext}
            aria-label="Next testimonials"
          >
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" fill="currentColor"/>
            </svg>
          </button>

          <div className="tst__viewport" role="region" aria-roledescription="carousel">
            <div
              className="tst__track"
              style={{ transform: `translateX(-${index * 100}%)` }}
            >
              {slides.map((group, gi) => (
                <div className="tst__slide" key={gi} aria-label={`Slide ${gi + 1}`}>
                  {group.map((item, i) => (
                    <article 
                      className={`tst-card ${isVisible ? 'is-visible' : ''}`} 
                      key={i}
                      style={{ animationDelay: `${gi * 0.2 + i * 0.1}s` }}
                    >
                      <div className="tst-card__glow" aria-hidden="true"></div>
                      <div className="tst-card__corner tst-card__corner--tl" aria-hidden="true"></div>
                      <div className="tst-card__corner tst-card__corner--br" aria-hidden="true"></div>
                      
              
                      <div className="tst-card__content">
                        <p className="tst-card__text">{item.quote}</p>

                        <div className="tst-card__author">
                          {/* Avatar div completely removed - only meta info remains */}
                          <div className="tst-card__meta">
                            <div className="tst-card__name">{item.name}</div>
                            <div className="tst-card__role">{item.role}</div>
                            <div className="tst-card__date">{item.date}</div>
                          </div>
                        </div>
                      </div>

                      <div className="tst-card__rating" aria-hidden="true">
                        {[...Array(5)].map((_, i) => (
                          <svg key={i} viewBox="0 0 24 24" width="16" height="16">
                            <path 
                              d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" 
                              fill="currentColor"
                            />
                          </svg>
                        ))}
                      </div>
                    </article>
                  ))}
                </div>
              ))}
            </div>
          </div>

          <div className="tst__progress" aria-hidden="true">
            <div 
              className="tst__progress-bar" 
              style={{ 
                animationDuration: '6s',
                animationPlayState: isHovering ? 'paused' : 'running'
              }}
            />
          </div>

          <div className="tst__dots" role="tablist" aria-label="Testimonials pagination">
            {slides.map((_, i) => (
              <button
                key={i}
                className={`tst__dot ${i === index ? "is-active" : ""}`}
                onClick={() => goToSlide(i)}
                aria-label={`Go to slide ${i + 1}`}
                aria-selected={i === index}
                role="tab"
              >
                <div className="tst__dot-fill"></div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}